import { proSavedWords } from "./saved-word.js";

/**
 * What a reader may upload, and how much of it is read.
 *
 * Beside `import-bounds.ts`, which bounds a paste. The two are separate because
 * they are bounded by different things. A paste is read inside the request that
 * carries it, so what bounds it is the handler's ten seconds and the size
 * constraint the web ACL puts on a request body. An upload is read by a
 * processor with fifteen minutes and no request in front of it, and what bounds
 * it is a number S3 enforces before any of this site's code runs.
 */

/**
 * The largest file S3 will accept, in bytes.
 *
 * Twenty megabytes. It is a decimal megabyte rather than a mebibyte because
 * this number is quoted to readers on the upload form, and the number a reader
 * sees beside their file in a file manager is decimal.
 *
 * **Chosen rather than inherited.** API Gateway's 10 MB and Lambda's 6 MB both
 * stop applying once the upload goes straight to the bucket, so nothing hands
 * this site a limit and one had to be picked. Twenty is about a kilobyte for
 * each of the {@link proSavedWords} a membership holds, which is generous for a
 * row of a vocabulary list: the site's own export of a full list with five
 * Chinese tags on every word measures 6.19 MB. An uploaded file runs fatter
 * than an export, an Anki collection carrying HTML in its fields, and this
 * clears that with room.
 *
 * **It is what lets the processor read the object whole.** A file this size
 * decodes into a string a 1 GB Lambda holds without difficulty, so nothing
 * downstream has to stream. Raising it is therefore not free, and the number to
 * check first is the processor's memory rather than this one.
 *
 * S3 applies it through `content-length-range` in the signed policy, which is
 * the reason the upload is a POST and never a PUT. A presigned PUT URL stores
 * an object of any size at all.
 */
export const uploadFileBytes = 20 * 1000 * 1000;

/**
 * How many records of an uploaded file are read.
 *
 * The cap on a member's list, because a candidate beyond it is one they could
 * never accept. The review screen already reports what the bounds left unread,
 * so a reader with a longer file is told rather than quietly given a prefix of
 * it.
 *
 * **Deliberately far above `proImportRows`.** That number bounds the work one
 * synchronous request does and this path has no request in it, so reusing it
 * would put a ten-second ceiling on a fifteen-minute job. `import-bounds.ts`
 * says the same thing from the other side.
 */
export const uploadImportRows = proSavedWords;

/**
 * How long a signed upload form stands.
 *
 * Fifteen minutes, which is long enough for somebody to go and find the file
 * they meant and short enough that a form left in a stale tab has to be
 * refreshed rather than used. The expiry is inside the signed policy, so S3
 * refuses the post rather than this site having to notice.
 */
export const uploadFormLifetimeMs = 15 * 60 * 1000;

/**
 * What every uploaded object is stored as, whatever the reader's file was.
 *
 * **Pinned by the form and matched exactly by the policy**, rather than read
 * off the file the reader chose. A POST policy condition is one exact match or
 * one prefix and there is no way to write a list, so conditioning on what a
 * browser reports would mean choosing between refusing the `.csv` that Windows
 * announces as `application/vnd.ms-excel` and accepting anything at all.
 *
 * Pinning it settles that and says something true. This site reads an uploaded
 * list as text and stores it labelled as the thing it reads. Nothing in the
 * bucket can then be served as markup or as a script, which costs nothing here
 * because the bucket is private and is one less thing to reason about.
 *
 * What it does not do is check the file is a vocabulary list. A reader who
 * uploads a photograph gets an import that found no words in it, which is the
 * same answer the paste box gives for the same input.
 */
export const uploadContentType = "text/plain";
